// ========================================
// BLOG HELPDESK (Index Page)
// ========================================

if (!customElements.get('custom-blog-helpdesk')) {
  class CustomBlogHelpdesk extends HTMLElement {
    constructor() {
      super();
      this.searchInput = null;
      this.articlesData = [];      // all articles from JSON endpoint
      this.categories = [];        // DOM category card elements
      this.resultsDropdown = null;
      this.subTopicFilters = null;
      this.articlesContainer = null;
      this.activeCategory = null;
      this.activeTag = null;
      this.defaultTopic = this.dataset.defaultTopic || '';
      this.blogHandle = this.dataset.blogHandle || '';
      this.hideTopicLabels = this.dataset.hideTopicLabels === 'true';
      this.lockToTopic = this.dataset.lockToTopic === 'true' && !!this.defaultTopic;
      // Subtopic lock: defaultSubtopic is the user-typed label (e.g. "Damplifier Pro").
      // We store both forms so we can match against article.tags (handleized) or labels.
      this.defaultSubtopic = (this.dataset.defaultSubtopic || '').trim();
      this.defaultSubtopicHandle = CustomBlogHelpdesk.handleize(this.defaultSubtopic);
      this.lockToSubtopic = this.dataset.lockToSubtopic === 'true' && !!this.defaultSubtopic;
      this.topicsByHandle = {};
      this.sortedTopics = [];
    }

    /* ---- Match Shopify's handleize filter closely enough for subtopic matching ---- */
    static handleize(s) {
      return (s || '')
        .toString()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    }

    connectedCallback() {
      this.parseAllTopics();
      Promise.all([
        this.loadTopicsFromEndpoint(),
        this.fetchAllArticles(),
      ])
        .then(() => {
          this.renderCategoryCards();
          this.initHelpdesk();
        })
        .catch((err) => {
          console.error('[CustomBlogHelpdesk] Init error:', err);
          this.initHelpdesk();
        });
    }

    /* ---- Topic metadata (from hidden JSON blob) ---- */

    parseAllTopics() {
      const el = this.querySelector('[data-all-topics]');
      if (!el) return;
      try {
        const raw = JSON.parse(el.textContent || '[]');
        raw.forEach((t) => {
          if (t && t.handle) this.topicsByHandle[t.handle] = t;
        });
      } catch (e) {
        console.error('[CustomBlogHelpdesk] Could not parse all-topics JSON', e);
      }
    }

    /* ---- Fetch which topic handles this blog uses ---- */

    async loadTopicsFromEndpoint() {
      if (!this.blogHandle) return;

      let page = 1;
      let totalPages = 1;
      const used = new Set();

      do {
        const res = await fetch(
          `/blogs/${this.blogHandle}?view=topics&page=${page}`,
          { method: 'GET', headers: { Accept: 'application/json' } }
        );
        if (!res.ok) throw new Error(`topics endpoint returned ${res.status}`);
        const data = await res.json();
        (data.topics || []).forEach((h) => used.add(h));
        totalPages = data.pages || 1;
        page++;
      } while (page <= totalPages);

      this.sortedTopics = Array.from(used)
        .map((h) => this.topicsByHandle[h])
        .filter(Boolean)
        .sort((a, b) => {
          const sa = typeof a.sort_order === 'number' ? a.sort_order : 999;
          const sb = typeof b.sort_order === 'number' ? b.sort_order : 999;
          if (sa !== sb) return sa - sb;
          return (a.name || '').localeCompare(b.name || '');
        });
    }

    /* ---- Fetch ALL articles from paginated JSON endpoint ---- */

    async fetchAllArticles() {
      if (!this.blogHandle) return;

      let page = 1;
      let totalPages = 1;
      const all = [];

      while (page <= totalPages) {
        const res = await fetch(`/blogs/${this.blogHandle}?view=json&page=${page}`);
        if (!res.ok) {
          console.warn('[CustomBlogHelpdesk] Could not fetch articles page', page);
          break;
        }
        const data = await res.json();
        all.push(...(data.articles || []));
        totalPages = data.pages || 1;
        page++;
      }

      // Only keep articles that have topics assigned
      this.articlesData = all.filter((a) => a.topics && a.topics.length > 0);
    }

    /* ---- Render topic category cards ---- */

    renderCategoryCards() {
      const container = this.querySelector('[data-helpdesk-categories]');
      if (!container || this.sortedTopics.length === 0) return;

      // Hide the category cards when ANY lock is active. Locked-to-topic
      // hides them because the topic is fixed; locked-to-subtopic alone hides
      // them because the page is showing a flat subtopic list across topics.
      if (this.lockToTopic || this.lockToSubtopic) {
        container.style.display = 'none';
        return;
      }

      const frag = document.createDocumentFragment();

      this.sortedTopics.forEach((topic, idx) => {
        const isDefault = this.defaultTopic
          ? topic.handle === this.defaultTopic
          : idx === 0;

        const card = document.createElement('a');
        card.href = `#${topic.handle}`;
        card.dataset.topic = topic.handle;
        card.setAttribute('role', 'button');
        card.className = `helpdesk-cat${isDefault ? ' active' : ''}`;

        if (topic.icon) {
          const img = document.createElement('img');
          img.src = topic.icon;
          img.width = 80;
          img.height = 80;
          img.alt = '';
          img.loading = 'lazy';
          card.appendChild(img);
        }

        const h3 = document.createElement('h3');
        h3.textContent = topic.name || topic.handle;
        if (this.hideTopicLabels) {
          h3.classList.add('visually-hidden');
        }
        card.appendChild(h3);

        frag.appendChild(card);
      });

      container.innerHTML = '';
      if (this.hideTopicLabels) {
        container.classList.add('hide-topic-labels');
      }
      container.appendChild(frag);
    }

    /* ---- Build a single article card (matches card-article-resource-center.liquid) ---- */

    buildArticleCard(article) {
      const ratio = 1;

      const card = document.createElement('article');
      // Note: .visible was removed; cards are visible by default and hidden via .is-hidden
      // so server-rendered cards (which don't carry .visible) display correctly without JS.
      card.className = 'helpdesk-article article-card article-card--standard f-column';
      card.dataset.topics = article.topics || '';
      card.dataset.tags = article.tags || '';
      card.dataset.tagsLabels = article.tags_labels || '';
      card.dataset.title = article.title || '';
      card.dataset.url = article.url || '';
      card.dataset.excerpt = article.excerpt || '';

      // Media
      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'article-card__media blocks-radius';
      const mediaLink = document.createElement('a');
      mediaLink.href = article.url;
      mediaLink.className = 'block media-wrapper hover-wrapper';
      const motionEl = document.createElement('div');
      motionEl.className = 'block';
      motionEl.style.cssText = `--aspect-ratio: ${ratio};`;

      if (article.image) {
        const img = document.createElement('img');
        img.src = article.image;
        img.alt = article.image_alt || article.title || '';
        img.loading = 'lazy';
        img.className = 'motion-reduce hover-scale-up';
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
        motionEl.appendChild(img);
      }

      mediaLink.appendChild(motionEl);
      mediaWrap.appendChild(mediaLink);
      card.appendChild(mediaWrap);

      // Content
      const contentWrap = document.createElement('div');
      contentWrap.className = 'article-card__content';
      const contentInner = document.createElement('div');
      contentInner.className = 'article-card__content-inner';

      // Subtopic tag
      if (article.first_subtopic) {
        const tagSpan = document.createElement('span');
        tagSpan.className = 'article-card__tags text-upper font-body-bolder text-subtext block';
        tagSpan.textContent = article.first_subtopic;
        contentInner.appendChild(tagSpan);
      }

      // Title
      const h3 = document.createElement('h3');
      h3.className = 'article-card__title h4';
      const titleLink = document.createElement('a');
      titleLink.className = 'reversed-link';
      titleLink.href = article.url;
      titleLink.textContent = article.title;
      h3.appendChild(titleLink);
      contentInner.appendChild(h3);

      // Excerpt
      if (article.excerpt) {
        const p = document.createElement('p');
        p.className = 'article-card__excerpt text-limit-2-lines text-subtext';
        p.textContent = article.excerpt;
        contentInner.appendChild(p);
      }

      // Downloads (mirrors card-article-resource-center.liquid + the article page)
      const hasDownloads = Array.isArray(article.downloads) && article.downloads.length > 0;
      if (hasDownloads) {
        const dlWrap = document.createElement('div');
        dlWrap.className = 'article-card__downloads product__downloads flex flex-col gap-3';

        const dlHeading = document.createElement('p');
        dlHeading.className = 'product__downloads-heading font-body-bolder text-normal m-0';
        dlHeading.textContent = 'Resources';
        dlWrap.appendChild(dlHeading);

        const dlList = document.createElement('ul');
        dlList.className = 'list-unstyled flex flex-col gap-1 m-0 p-0';

        const downloadIcon =
          '<svg class="icon icon-download icon--2xs icon--thick shrink-0" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.5 12.5V15.8333C17.5 16.7538 16.7538 17.5 15.8333 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V12.5" stroke="currentcolor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.83333 8.33333L10 12.5L14.1667 8.33333" stroke="currentcolor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 12.5V2.5" stroke="currentcolor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const videoIcon =
          '<svg class="icon icon-video icon--2xs icon--thick shrink-0" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 8.75L8.75 6.25V11.25L12.5 8.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.875 3.75H3.125C2.77982 3.75 2.5 4.02982 2.5 4.375V13.125C2.5 13.4702 2.77982 13.75 3.125 13.75H16.875C17.2202 13.75 17.5 13.4702 17.5 13.125V4.375C17.5 4.02982 17.2202 3.75 16.875 3.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.5 16.25H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        const appendLink = (dl, isVideo) => {
          if (!dl || !dl.url) return;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = dl.url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.className =
            'btn btn--plain product__download-link inline-flex items-center gap-2' +
            (isVideo ? ' product__video-link' : '');
          a.innerHTML = isVideo ? videoIcon : downloadIcon;
          const span = document.createElement('span');
          span.className = 'font-body text-normal';
          span.textContent = dl.label || (isVideo ? 'Watch Video' : 'Download');
          a.appendChild(span);
          li.appendChild(a);
          dlList.appendChild(li);
        };

        // Downloads (files) first, then video links — matches the Liquid renderers.
        article.downloads
          .filter((dl) => dl && dl.type !== 'video')
          .forEach((dl) => appendLink(dl, false));
        article.downloads
          .filter((dl) => dl && dl.type === 'video')
          .forEach((dl) => appendLink(dl, true));

        dlWrap.appendChild(dlList);
        contentInner.appendChild(dlWrap);
      }

      // Button — hidden when the card surfaces downloads (the downloads are the CTA)
      if (!hasDownloads) {
        const btn = document.createElement('a');
        btn.href = article.url;
        btn.className = 'btn btn--icon article-card__button';
        const btnText = document.createElement('span');
        btnText.className = 'btn__text';
        btnText.textContent = 'Read more';
        btn.appendChild(btnText);
        contentInner.appendChild(btn);
      }

      contentWrap.appendChild(contentInner);
      card.appendChild(contentWrap);

      return card;
    }

    /* ---- Filter predicate (shared by render + search pool) ---- */

    matchesActiveFilter(article) {
      // Topic filter — applied only when an activeCategory is set. With
      // lock_to_subtopic alone (no topic lock), activeCategory may be null
      // and we want a flat list across all topics.
      if (this.activeCategory) {
        const topics = (article.topics || '').split(',');
        if (!topics.includes(this.activeCategory)) return false;
      }
      // Subtopic / tag filter — applied when locked, when user picked a pill,
      // or both. Match against handleized tags first, fall back to labels.
      if (this.activeTag) {
        const tags = (article.tags || '').split(',');
        const labels = (article.tags_labels || '').split(',');
        if (!tags.includes(this.activeTag) && !labels.includes(this.activeTag)) return false;
      }
      return true;
    }

    /* ---- Render article cards for the active topic + tag ----
       Append-not-replace: reuses server-rendered cards (and previously hydrated
       cards) matched by data-url, toggling .is-hidden for the active filter.
       Only articles not already in the DOM are built and appended. This avoids
       the SSR cards getting wiped on hydration, which would cause a visible
       flicker between first paint and JS data load. */

    renderArticles() {
      if (!this.articlesContainer) return;

      // Index existing DOM cards by URL (covers SSR cards + previously hydrated cards).
      const existing = new Map();
      this.articlesContainer.querySelectorAll('article[data-url]').forEach((card) => {
        existing.set(card.dataset.url, card);
      });

      const frag = document.createDocumentFragment();
      let visibleCount = 0;

      this.articlesData.forEach((article) => {
        const isVisible = this.matchesActiveFilter(article);
        if (isVisible) visibleCount++;

        let card = existing.get(article.url);
        if (card) {
          card.classList.toggle('is-hidden', !isVisible);
        } else {
          card = this.buildArticleCard(article);
          card.classList.toggle('is-hidden', !isVisible);
          frag.appendChild(card);
        }
      });

      if (frag.children.length > 0) {
        this.articlesContainer.appendChild(frag);
      }

      // Toggle the server-rendered empty-state message (hidden by default).
      const emptyMsg = this.articlesContainer.querySelector('[data-empty-msg]');
      if (emptyMsg) {
        emptyMsg.hidden = visibleCount > 0;
      }
    }

    /* ---- Init (search, category clicks, hash routing) ---- */

    initHelpdesk() {
      this.searchInput = this.querySelector('#helpdeskSearch');
      this.resultsDropdown = this.querySelector('.ui-autocomplete');
      this.categories = Array.from(this.querySelectorAll('.helpdesk-cat'));
      this.subTopicFilters = this.querySelector('#subTopicFilters');
      this.articlesContainer = this.querySelector('[data-helpdesk-articles]');

      // Remove loading indicator
      const loader = this.querySelector('[data-articles-loading]');
      if (loader) loader.remove();

      // Search
      this.searchInput.addEventListener('input', (e) => {
        this.showSearchResults(e.target.value);
      });

      document.addEventListener('mousedown', (e) => {
        const searchWrap = this.querySelector('.relative-search-wrap');
        if (searchWrap && !searchWrap.contains(e.target)) {
          this.closeAndClear();
        }
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAndClear();
          this.searchInput.blur();
        }
      });

      // Category clicks (skipped when either lock is active — cards are hidden anyway)
      if (!this.lockToTopic && !this.lockToSubtopic) {
        this.categories.forEach((cat) =>
          cat.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleCategoryClick(cat);
          })
        );

        window.addEventListener('hashchange', () => {
          this.loadCategoryFromHash();
        });
      }

      this.loadCategoryFromHash();
    }

    closeAndClear() {
      this.searchInput.value = '';
      this.resultsDropdown.innerHTML = '';
      this.resultsDropdown.style.display = 'none';
    }

    loadCategoryFromHash() {
      // Locked mode: pin topic and/or subtopic, ignore hash. Cards/pills are
      // hidden by render methods when their respective locks are active.
      if (this.lockToTopic || this.lockToSubtopic) {
        this.activeCategory = this.lockToTopic ? this.defaultTopic : null;
        this.activeTag = this.lockToSubtopic ? this.defaultSubtopicHandle : null;
        this.renderArticles();
        this.buildSubTopicFilters();
        return;
      }

      const hash = window.location.hash.slice(1);

      if (hash) {
        const matchingCategory = this.categories.find(cat => cat.dataset.topic === hash);
        if (matchingCategory) {
          this.categories.forEach(cat => cat.classList.remove('active'));
          matchingCategory.classList.add('active');
          this.activeCategory = hash;
          this.activeTag = null;
          this.renderArticles();
          this.buildSubTopicFilters();
          return;
        }
      }

      if (this.defaultTopic) {
        const defaultCategory = this.categories.find(cat => cat.dataset.topic === this.defaultTopic);
        if (defaultCategory) {
          this.categories.forEach(cat => cat.classList.remove('active'));
          defaultCategory.classList.add('active');
          this.activeCategory = this.defaultTopic;
          this.renderArticles();
          this.buildSubTopicFilters();
          return;
        }
      }

      const activeCat = this.querySelector('.helpdesk-cat.active');
      if (activeCat) {
        this.activeCategory = activeCat.dataset.topic;
        this.renderArticles();
        this.buildSubTopicFilters();
      }
    }

    /* ---- Search (operates on in-memory articlesData) ---- */

    showSearchResults(query) {
      this.resultsDropdown.innerHTML = '';

      if (!query || query.length < 2) {
        this.resultsDropdown.style.display = 'none';
        return;
      }

      const lowerQuery = query.toLowerCase();

      // Narrow the search pool to whatever is locked. Both locks compose.
      let searchPool = this.articlesData;
      if (this.lockToTopic) {
        searchPool = searchPool.filter((a) => (a.topics || '').split(',').includes(this.activeCategory));
      }
      if (this.lockToSubtopic) {
        searchPool = searchPool.filter((a) => {
          const tags = (a.tags || '').split(',');
          const labels = (a.tags_labels || '').split(',');
          return tags.includes(this.activeTag) || labels.includes(this.activeTag);
        });
      }

      const filteredResults = searchPool
        .map((item) => {
          const titleLower = (item.title || '').toLowerCase();
          const excerptLower = (item.excerpt || '').toLowerCase();
          let score = 0;
          if (titleLower === lowerQuery) score = 4;
          else if (titleLower.startsWith(lowerQuery)) score = 3;
          else if (titleLower.includes(lowerQuery)) score = 2;
          else if (excerptLower.includes(lowerQuery)) score = 1;
          return { ...item, score };
        })
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score);

      if (filteredResults.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      filteredResults.slice(0, 50).forEach((item) => {
        const resultItem = document.createElement('div');
        resultItem.classList.add('result-item');
        resultItem.innerHTML = `<strong>${this.highlightMatch(item.title, query)}</strong>`;
        resultItem.addEventListener('click', () => {
          window.location.href = item.url;
        });
        this.resultsDropdown.appendChild(resultItem);
      });

      this.resultsDropdown.style.display = 'block';
    }

    highlightMatch(text, query) {
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /* ---- Category + subtopic interaction ---- */

    handleCategoryClick(categoryElement) {
      const topic = categoryElement.dataset.topic;

      this.categories.forEach((cat) =>
        cat.classList.toggle('active', cat === categoryElement)
      );

      this.activeCategory = topic;
      this.activeTag = null;

      this.renderArticles();
      this.buildSubTopicFilters();

      window.location.hash = topic;

      if (this.articlesContainer) {
        window.scrollTo({
          top: this.articlesContainer.offsetTop - 120,
          behavior: 'smooth',
        });
      }
    }

    buildSubTopicFilters() {
      this.subTopicFilters.innerHTML = '';

      // When locked to a subtopic, the pills are meaningless — keep them hidden.
      if (this.lockToSubtopic) {
        this.subTopicFilters.classList.remove('visible');
        this.updateHeaderSpacing();
        return;
      }

      if (!this.activeCategory) return;

      // Build tag map from in-memory data
      const tagLabelMap = {};
      this.articlesData.forEach((article) => {
        const topics = (article.topics || '').split(',');
        if (!topics.includes(this.activeCategory)) return;
        const handles = (article.tags || '').split(',').filter(t => t.trim() !== '');
        const labels = (article.tags_labels || '').split(',').filter(t => t.trim() !== '');
        handles.forEach((handle, i) => {
          if (!tagLabelMap[handle]) tagLabelMap[handle] = labels[i] || handle;
        });
      });

      const allTags = new Set(Object.keys(tagLabelMap));

      if (allTags.size > 0) {
        const allTag = document.createElement('button');
        allTag.classList.add('subtopic-tag');
        if (!this.activeTag) allTag.classList.add('active');
        allTag.textContent = 'All';
        allTag.addEventListener('click', () => this.handleTagClick(null));
        this.subTopicFilters.appendChild(allTag);

        Array.from(allTags).sort().forEach((tag) => {
          const tagButton = document.createElement('button');
          tagButton.classList.add('subtopic-tag');
          if (this.activeTag === tag) tagButton.classList.add('active');
          tagButton.textContent = tagLabelMap[tag] || tag;
          tagButton.dataset.tag = tag;
          tagButton.addEventListener('click', () => this.handleTagClick(tag));
          this.subTopicFilters.appendChild(tagButton);
        });

        this.subTopicFilters.classList.add('visible');
      } else {
        this.subTopicFilters.classList.remove('visible');
      }

      this.updateHeaderSpacing();
    }

    /* ---- Add spacing class when nothing sits between the header and articles ---- */

    updateHeaderSpacing() {
      const categoriesHidden = this.lockToTopic || this.lockToSubtopic;
      const subtopicsHidden = !this.subTopicFilters.classList.contains('visible');
      this.classList.toggle('no-nav-between', categoriesHidden && subtopicsHidden);
    }

    handleTagClick(tag) {
      this.activeTag = tag;

      const tagButtons = this.subTopicFilters.querySelectorAll('.subtopic-tag');
      tagButtons.forEach((btn) => {
        if (tag === null) {
          btn.classList.toggle('active', btn.textContent === 'All');
        } else {
          btn.classList.toggle('active', btn.dataset.tag === tag);
        }
      });

      this.renderArticles();
    }
  }

  customElements.define('custom-blog-helpdesk', CustomBlogHelpdesk);
}

// ========================================
// HELPDESK ARTICLE NAV (Individual Article Page)
// ========================================

if (!customElements.get('helpdesk-article-nav')) {
  class HelpdeskArticleNav extends HTMLElement {
    constructor() {
      super();
      this.searchInput = null;
      this.resultsDropdown = null;
      this.blogHandle = null;
      this.articlesData = [];
    }

    connectedCallback() {
      this.initArticleNav();
    }

    initArticleNav() {
      this.blogHandle = this.dataset.blogHandle;
      this.searchInput = this.querySelector('#helpdeskArticleSearch');
      this.resultsDropdown = this.querySelector('.ui-autocomplete');

      if (!this.searchInput || !this.resultsDropdown) return;

      this.fetchAllArticles();

      this.searchInput.addEventListener('input', (e) => {
        this.showSearchResults(e.target.value);
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAndClear();
        }
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeAndClear();
        }
      });
    }

    closeAndClear() {
      this.searchInput.value = '';
      this.resultsDropdown.innerHTML = '';
      this.resultsDropdown.style.display = 'none';
    }

    async fetchAllArticles() {
      try {
        let page = 1;
        let totalPages = 1;
        const allArticles = [];

        while (page <= totalPages) {
          const response = await fetch(`/blogs/${this.blogHandle}?view=json&page=${page}`);
          if (!response.ok) {
            console.warn('Could not fetch blog articles JSON');
            break;
          }
          const data = await response.json();
          allArticles.push(...(data.articles || []));
          totalPages = data.pages || 1;
          page++;
        }

        this.articlesData = allArticles;
      } catch (error) {
        console.error('Error fetching blog articles:', error);
        this.articlesData = [];
      }
    }

    showSearchResults(query) {
      this.resultsDropdown.innerHTML = '';

      if (!query || query.length < 2) {
        this.resultsDropdown.style.display = 'none';
        return;
      }

      if (!this.articlesData || this.articlesData.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">Search not available</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      const lowerQuery = query.toLowerCase();
      const filteredResults = this.articlesData
        .map((article) => {
          const titleLower = article.title.toLowerCase();
          const excerptLower = article.excerpt ? article.excerpt.toLowerCase() : '';
          let score = 0;
          if (titleLower === lowerQuery) score = 4;
          else if (titleLower.startsWith(lowerQuery)) score = 3;
          else if (titleLower.includes(lowerQuery)) score = 2;
          else if (excerptLower.includes(lowerQuery)) score = 1;
          return { ...article, score };
        })
        .filter((article) => article.score > 0)
        .sort((a, b) => b.score - a.score);

      if (filteredResults.length === 0) {
        this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
        this.resultsDropdown.style.display = 'block';
        return;
      }

      filteredResults.slice(0, 50).forEach((article) => {
        const resultItem = document.createElement('div');
        resultItem.classList.add('result-item');
        resultItem.innerHTML = `<strong>${this.highlightMatch(article.title, query)}</strong>`;
        resultItem.addEventListener('click', () => {
          window.location.href = article.url;
        });
        this.resultsDropdown.appendChild(resultItem);
      });

      this.resultsDropdown.style.display = 'block';
    }

    highlightMatch(text, query) {
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }

  customElements.define('helpdesk-article-nav', HelpdeskArticleNav);
}